export type BuildStatus = 'running' | 'succeeded' | 'failed' | 'interrupted'

/** The three steps the professor reviews, in order. */
export type ReviewStep = 'sidecar' | 'stocks' | 'links'

/**
 * 'finalize' runs on approving the links and proposes the drift rules.
 * 'proposals' and 'artifacts' run during play, on one round.
 */
export type BuildStep = ReviewStep | 'finalize' | 'proposals' | 'artifacts'

export interface WorldGraphBuild {
  id: number
  step: BuildStep
  status: BuildStatus
  started_at: string
  finished_at: string | null
  elapsed_seconds: number
  progress_chars: number
  provider: string
  key_source: 'room' | 'server' | ''
  error: string
  notes: string[]
  round_number: number | null
}

export interface WorldGraphTeam {
  id: string
  name: string
}

/** One of the room's own teams, which players are assigned to. */
export interface RoomTeam {
  id: number
  name: string
  members: number
}

/** A roster team and the room team that plays it. */
export interface TeamSetupEntry {
  id: string
  name: string
  team: RoomTeam | null
  /** An unlinked roster team's same-named room team, if one is free. */
  suggested_team: RoomTeam | null
}

export interface TeamSetup {
  teams: TeamSetupEntry[]
  room_teams: RoomTeam[]
  complete: boolean
}

export type StockType = 'Lever' | 'Level' | 'Accumulator' | 'Drifter'

export const STOCK_TYPES: StockType[] = [
  'Lever',
  'Level',
  'Accumulator',
  'Drifter',
]

/** A stock: one quantity in the world, on a 0-100 scale. */
export interface WorldGraphNode {
  id: string
  label: string
  category: string
  type: StockType
  value: number
  inverted: boolean
  baseline_value?: number
  owner: string | null
  scope?: string | null
  concept?: string | null
  measures?: string
  rationale?: string
}

/** A causal link: the source pushes the target, this strongly, after this many rounds. */
export interface WorldGraphEdge {
  id: string
  source: string
  target: string
  sign: '+' | '-'
  magnitude: number
  lag: number
  mechanism: string
}

export interface DriftRule {
  id: string
  target: string
  delta: number
  condition: string
  start_round: number
  rationale: string
}

/** One decision a player submitted in the round. */
export interface RoundDecision {
  id: number
  student: string
  text: string
  /** The professor's ruling, the only text the model reads; empty until adjudicated. */
  adjudication?: string
  late: boolean
}

/** A team's decisions in a round and the stock changes proposed for them. */
export interface TeamProposal {
  key: string
  team_id: number | null
  team_name: string
  roster_team: string | null
  roster_team_name: string
  decisions: RoundDecision[]
  status: 'pending' | 'proposed' | 'failed' | 'no_team' | 'waiting'
  include: boolean
  nodes_delta: Record<string, number>
  justifications: {
    node_id: string
    decision_ref?: string
    rationale?: string
    cross_team?: boolean
    confidence?: string
  }[]
  skipped: { reference?: string; reason?: string }[]
  rejected: { node_id: string; reason: string }[]
  notes: string
  error: string
}

/** One stock change applying a round made, and where it came from. */
export interface RoundLogEntry {
  node_id: string
  change_source:
    | 'player_decision'
    | 'white_cell_event'
    | 'tick_rule'
    | 'propagation'
  old_value?: number
  new_value?: number
  delta?: number
  rejected?: string
}

export interface ArtifactAudience {
  all: boolean
  teams: number[]
}

/** A news item or brief written about the round, before or after release. */
export interface RoundArtifact {
  id: string
  title: string
  text: string
  product_type: string
  source_stock: string
  audience: ArtifactAudience
  include: boolean
  event_id: number | null
  released_at: string | null
}

export interface WorldGraphRound {
  round_number: number
  status: 'open' | 'applied'
  proposals: TeamProposal[]
  log: RoundLogEntry[]
  applied_at: string | null
  transitions: { stock: string; label: string }[]
  artifacts: RoundArtifact[]
}

export interface WorldGraphState {
  room_id: string
  enabled: boolean
  scenario_document: string
  sidecar: string
  sidecar_approved: boolean
  teams: WorldGraphTeam[]
  team_setup: TeamSetup
  graph: { nodes: WorldGraphNode[]; edges: WorldGraphEdge[] }
  stocks_approved: boolean
  links_approved: boolean
  tick_rules: DriftRule[]
  ready: boolean
  /** Rounds have been played, so the setup steps can't be re-opened. */
  setup_locked: boolean
  checks: { errors: string[]; warnings: string[] }
  current_build: WorldGraphBuild | null
  game_round: number
  round_options: { round_number: number; decisions: number }[]
  rounds: WorldGraphRound[]
}
